/**
 * Pure section-outline geometry shared by the chart designer, the storefront
 * seat map, the minimap and the SVG export. No canvas or DOM dependencies, so
 * it is safe in API routes and in plain unit tests.
 *
 * A polygon section may carry per-edge bends (drag-to-bend in the designer):
 *   edgeCurves[i] — signed bow of the edge points[i] -> points[i+1], perpendicular
 *                   to the edge (0 = straight)
 *   edgeSkew[i]   — how far the bow's peak slides along the edge (0 = centred)
 * The drawn edge is a quadratic curve whose PEAK sits at the designer's drag
 * handle (midpoint + tangent*skew + normal*bend). Everything that reasons about
 * the outline as points (hit-testing, bounds, placeholder dots, SVG) must use
 * the same curve the canvas traces, which is what these helpers guarantee.
 */

export type Point = [number, number]

/** True when at least one edge is bowed or has its peak slid. */
export function hasEdgeCurves(edgeCurves?: number[], edgeSkew?: number[]): boolean {
  return !!((edgeCurves && edgeCurves.some(c => c)) || (edgeSkew && edgeSkew.some(s => s)))
}

/**
 * Quadratic control point for the edge p0 -> p1 bowed by `bend` with its peak
 * slid `skew` along the edge. A quadratic curve peaks halfway between the chord
 * midpoint and its control point, so the control point is 2x the handle offset —
 * that keeps the drawn peak exactly under the designer's handle.
 */
export function polygonEdgeControlPoint(p0: number[], p1: number[], bend: number, skew: number): Point {
  const mx = (p0[0] + p1[0]) / 2, my = (p0[1] + p1[1]) / 2
  const dx = p1[0] - p0[0], dy = p1[1] - p0[1]
  const len = Math.hypot(dx, dy) || 1
  const tx = dx / len, ty = dy / len       // along-edge (slide)
  const nx = -dy / len, ny = dx / len       // perpendicular (bend)
  return [mx + tx * skew * 2 + nx * bend * 2, my + ty * skew * 2 + ny * bend * 2]
}

/**
 * The polygon's drawn outline as a plain vertex list. Straight edges are kept
 * as-is; bowed edges are sampled along the same quadratic the canvas draws
 * (`segments` pieces per bowed edge). Returns the ORIGINAL array when nothing is
 * bowed, so callers can pass it straight through without copying.
 */
export function flattenPolygonOutline(
  pts: number[][],
  edgeCurves?: number[],
  edgeSkew?: number[],
  segments = 12,
): number[][] {
  if (pts.length < 3 || !hasEdgeCurves(edgeCurves, edgeSkew)) return pts
  const out: number[][] = []
  const n = Math.max(2, Math.floor(segments))
  for (let i = 0; i < pts.length; i++) {
    const p0 = pts[i], p1 = pts[(i + 1) % pts.length]
    out.push(p0)
    const b = edgeCurves?.[i] || 0
    const sk = edgeSkew?.[i] || 0
    if (!b && !sk) continue
    const [cx, cy] = polygonEdgeControlPoint(p0, p1, b, sk)
    for (let k = 1; k < n; k++) {
      const t = k / n, u = 1 - t
      out.push([
        u * u * p0[0] + 2 * u * t * cx + t * t * p1[0],
        u * u * p0[1] + 2 * u * t * cy + t * t * p1[1],
      ])
    }
  }
  return out
}

/**
 * SVG path data for the outline (`M … L/Q … Z`), using the same control points
 * the canvas uses so the SVG export matches the live map pixel for pixel.
 */
export function polygonOutlineSvgPath(
  pts: number[][],
  edgeCurves?: number[],
  edgeSkew?: number[],
  round: (n: number) => number | string = n => Math.round(n * 100) / 100,
): string {
  if (pts.length < 3) return ''
  const parts: string[] = [`M ${round(pts[0][0])} ${round(pts[0][1])}`]
  for (let i = 0; i < pts.length; i++) {
    const p0 = pts[i], p1 = pts[(i + 1) % pts.length]
    const b = edgeCurves?.[i] || 0
    const sk = edgeSkew?.[i] || 0
    if (!b && !sk) {
      parts.push(`L ${round(p1[0])} ${round(p1[1])}`)
    } else {
      const [cx, cy] = polygonEdgeControlPoint(p0, p1, b, sk)
      parts.push(`Q ${round(cx)} ${round(cy)} ${round(p1[0])} ${round(p1[1])}`)
    }
  }
  parts.push('Z')
  return parts.join(' ')
}
